import React from 'react';
import { Pencil } from 'lucide-react';
import { Resource } from '../../lib/resources';

interface CompensationSectionProps {
  resource: Resource;
  editMode: string | null;
  formData: Partial<Resource>;
  setFormData: (data: Partial<Resource>) => void;
  onSave: (section: string) => void;
  onEdit: (section: string) => void;
  loading?: boolean;
}

export default function CompensationSection({
  resource,
  editMode,
  formData,
  setFormData,
  onSave,
  onEdit,
  loading
}: CompensationSectionProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">Compensation</h3>
        {editMode === 'compensation' ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit('')}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave('compensation')}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => onEdit('compensation')}
            className="text-gray-400 hover:text-gray-600"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-4">
        <div className="space-y-6">
          {editMode === 'compensation' ? (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Compensation Type
                </label>
                <select
                  value={formData.compensation_type || 'hourly'}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    compensation_type: e.target.value as 'hourly' | 'flat_rate' | 'commission'
                  })}
                  className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                >
                  <option value="hourly">Hourly Rate</option>
                  <option value="flat_rate">Flat Rate</option>
                  <option value="commission">Commission</option>
                </select>
              </div>

              {formData.compensation_type === 'hourly' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Hourly Rate
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.hourly_rate || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        hourly_rate: parseFloat(e.target.value) 
                      })}
                      className="w-full pl-7 pr-4 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>
              )}

              {formData.compensation_type === 'flat_rate' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Flat Rate Amount
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.flat_rate_amount || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        flat_rate_amount: parseFloat(e.target.value) 
                      })}
                      className="w-full pl-7 pr-4 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                  </div>
                </div>
              )}

              {formData.compensation_type === 'commission' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Commission Percentage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      value={formData.commission_percentage || formData.commission_rate || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        commission_percentage: parseFloat(e.target.value),
                        commission_rate: parseFloat(e.target.value)
                      })}
                      className="w-full pr-8 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">%</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <div className="text-xs text-gray-500">Compensation Type</div>
                <div className="text-sm text-gray-900 mt-1 capitalize">
                  {resource.compensation_type.replace('_', ' ')}
                </div>
              </div>

              {resource.compensation_type === 'hourly' && (
                <div>
                  <div className="text-xs text-gray-500">Hourly Rate</div>
                  <div className="text-sm text-gray-900 mt-1">
                    ${resource.hourly_rate.toFixed(2)}/hour
                  </div>
                </div>
              )}

              {resource.compensation_type === 'flat_rate' && (
                <div>
                  <div className="text-xs text-gray-500">Flat Rate Amount</div>
                  <div className="text-sm text-gray-900 mt-1">
                    ${resource.flat_rate_amount.toFixed(2)}
                  </div>
                </div>
              )}

              {resource.compensation_type === 'commission' && (
                <div>
                  <div className="text-xs text-gray-500">Commission Rate</div>
                  <div className="text-sm text-gray-900 mt-1">
                    {(resource.commission_percentage || resource.commission_rate || 0)}%
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}